/**
 * Shared, dependency-free helpers for editing PDP picker rows AFTER the set was
 * confirmed — the "1 image at a time" flow lets the user re-open the Customize
 * popover on rows the queue has not reached yet. (POR-13869)
 *
 * Kept pure (no Vue, no Vuex, no axios) so the picker widget and its host
 * (NetworksPageV2) share ONE definition of what a customization patch contains
 * and cannot drift apart.
 *
 * Rows are always addressed by ARRAY POSITION, never by `row.rowIndex`:
 * duplicate slots (POR-13854) allow two rows of the same type, and the
 * sequential queue indexes by position too (queue.rowSelections[currentIndex]).
 */

export const PICKER_WIDGET_ID = 'pdp_image_set_picker';

/**
 * Exactly the fields the Customize popover owns. Anything the queue adds at run
 * time (gs1Approach, reference_image_url, template_id, mode, ...) is deliberately
 * absent so a late edit can never clobber it.
 */
export const ROW_CUSTOMIZATION_FIELDS = [
  'userInstructions',
  'overlayText',
  'overlayTextMode',
  'brandLogoUrl',
  'brandLogoName',
  'productImageUrl',
  'productImageName',
  'productImageUrls',
  'productImageNames',
];

/**
 * Build the full patch for one row. Every field is always present — a cleared
 * Direction or a removed logo has to propagate as '' / null, not vanish.
 *
 * `productImages` is the already-resolved list (per-tile picks, or the
 * "use for all" fallback); the caller owns that precedence, exactly as the
 * picker's confirm() does.
 */
export function buildRowCustomizationPatch({ row = {}, brandLogo = null, productImages = [] } = {}) {
  const safeRow = row || {};
  const images = (Array.isArray(productImages) ? productImages : []).filter((im) => im && im.url);

  return {
    userInstructions: safeRow.userInstructions || '',
    overlayText: safeRow.overlayText || '',
    overlayTextMode: safeRow.overlayTextMode || 'editable',
    brandLogoUrl: (brandLogo && brandLogo.url) || null,
    brandLogoName: (brandLogo && brandLogo.name) || '',
    productImageUrl: images.length ? images[0].url : null,
    productImageName: images.length ? (images[0].name || '') : '',
    productImageUrls: images.map((im) => im.url),
    productImageNames: images.map((im) => im.name || ''),
  };
}

/**
 * Merge an allow-listed patch into ONE row of a rows array, by array position.
 * Returns the original reference untouched when the index is out of range, so a
 * stale emit can never corrupt the set.
 */
export function applyRowCustomizationToRows(rows, rowIndex, patch) {
  if (!Array.isArray(rows)) return rows;
  if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= rows.length) return rows;

  const source = patch || {};
  const allowed = {};

  ROW_CUSTOMIZATION_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      allowed[key] = source[key];
    }
  });

  return rows.map((row, i) => (i === rowIndex ? { ...row, ...allowed } : row));
}

/**
 * Rehydrate the picker's brand-logo slot from a persisted row, so the popover
 * still shows an attached logo after a page reload. (The product-image slot is
 * already seeded in the widget's created() hook — POR-13810.)
 */
export function brandLogoFromRow(row) {
  if (!row || !row.brandLogoUrl) return null;
  return { url: row.brandLogoUrl, name: row.brandLogoName || '' };
}

/**
 * Index of the LAST message carrying a picker widget — i.e. the most recent set.
 * Older pickers from earlier sets in the same conversation keep their own state.
 */
export function findPickerWidgetMessageIndex(messages) {
  const list = Array.isArray(messages) ? messages : [];

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const widgets = (list[i] && list[i].widgets) || [];
    if (Array.isArray(widgets) && widgets.some((w) => w && w.id === PICKER_WIDGET_ID)) return i;
  }

  return -1;
}

/** Stamp the sequential cursor onto the picker widget of a widgets array. */
export function withPickerSequentialCursor(widgets, cursor) {
  const list = Array.isArray(widgets) ? widgets : [];
  return list.map((w) => ((w && w.id === PICKER_WIDGET_ID) ? { ...w, sequentialCursor: cursor } : w));
}
